import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import FileDropZone from "./FileDropZone";

/* ============================================================
   SHIPPER PORTAL — Customs Brokers tab

   The shipper sees ONLY brokers they themselves created
   (createdBy === currentShipper.id). They can add new brokers
   from this screen; the new broker lands in the shared
   gfi-brokers store with createdBy stamped, so Arturo sees it in
   his CustomsBrokerRegistry alongside his own entries with no
   extra assignment step.

   The form below is duplicated from CustomsBrokerRegistry per
   CLAUDE.md ("intentionally not shared across registries; do
   not extract them into a common module unless asked"). Every
   field, section, validation, and layout decision should stay
   in sync with that registry — only the save-time createdBy
   stamp differs.
   ============================================================ */

const STORAGE_KEY = "gfi-brokers";

const T = {
  en: {
    /* portal shell */
    title: "Customs Brokers · Shipper Portal",
    subtitle: "Brokers you've registered. Arturo will see these in his system automatically.",
    loggedAs: "Logged in as",
    add: "New broker", edit: "Edit broker",
    noResults: "You haven't registered any customs brokers yet. Create the first one with the button above.",
    search: "Search by name, license, or port...",
    cantEditBanner: "This broker was created by GFI. View only — contact Arturo to modify it.",
    /* list columns */
    colInitials: "Initials", colShort: "Short Name", colFull: "Name / Company",
    colType: "Type", colPhone: "Phone", colEmail: "Email", colCountry: "Country", colCity: "City",
    /* form — mirrors CustomsBrokerRegistry */
    initials: "Initials", shortName: "Short name", fullName: "Full name (legal)",
    agentType: "Agent type", mexicanAgent: "Mexican Agent", americanAgent: "American Agent",
    license: "License #", patente: "Patent #",
    rfc: "RFC", rfcPart1: "Letters", rfcPart2: "Date", rfcPart3: "Homoclave", rfcSub: "Registro Federal de Contribuyentes",
    nombre: "Name(s)", apPaterno: "Last name (paternal)", apMaterno: "Last name (maternal)",
    firstName: "First name", middleName: "Middle name", lastName: "Last name",
    phone: "Phone", email: "Email", countryCode: "Code", areaCode: "Area", phoneNumber: "Number",
    address: "Address", colony: "Colony / Neighborhood", city: "City", state: "State", country: "Country", zip: "Zip code",
    customsPort: "Customs port", defaultWarehouse: "Default warehouse",
    services: "Services offered", status: "Status", notes: "Notes",
    identity: "Identity", contactInfo: "Contact information", location: "Location",
    fiscal: "Fiscal information", operations: "Operations",
    contacts: "Contacts", addContact: "Add contact", removeContact: "Remove", noContacts: "No contacts added",
    documents: "Documents", addDoc: "Upload document", noDocuments: "No documents uploaded",
    viewDoc: "View", downloadDoc: "Download", removeDoc: "Remove",
    save: "Save", cancel: "Cancel", delete: "Delete", close: "Close",
    confirmDel: "Delete this broker?", yes: "Yes, delete", no: "Cancel",
    confirmExit: "You have unsaved changes. Exit without saving?", yesExit: "Yes, exit", noExit: "Keep editing",
    saved: "Broker saved successfully", deleted: "Broker deleted",
    required: "Required", duplicateInit: "Initials already exist", duplicateShort: "Short name already exists",
    invalidRfc: "Invalid RFC format", fillRequired: "Please fill all required fields",
    active: "Active", inactive: "Inactive",
    svc_import: "Import Clearance", svc_export: "Export Clearance", svc_fumigation: "Fumigation",
    svc_inspection: "USDA Inspection", svc_cold: "Cold Treatment", svc_phyto: "Phytosanitary Certs",
    svc_organic: "Organic Documentation", svc_transport: "Cross-border Transport",
  },
  es: {
    title: "Agentes Aduanales · Portal del Exportador",
    subtitle: "Agentes que tú diste de alta. Arturo verá estos en su sistema automáticamente.",
    loggedAs: "Conectado como",
    add: "Nuevo agente", edit: "Editar agente",
    noResults: "Aún no has registrado agentes aduanales. Crea el primero con el botón superior.",
    search: "Buscar por nombre, licencia o puerto...",
    cantEditBanner: "Este agente fue creado por GFI. Solo lectura — contacta a Arturo para modificarlo.",
    colInitials: "Iniciales", colShort: "Nombre Corto", colFull: "Nombre / Empresa",
    colType: "Tipo", colPhone: "Teléfono", colEmail: "Email", colCountry: "País", colCity: "Ciudad",
    initials: "Iniciales", shortName: "Nombre corto", fullName: "Nombre completo (legal)",
    agentType: "Tipo de agente", mexicanAgent: "Agente Mexicano", americanAgent: "Agente Americano",
    license: "Licencia #", patente: "Patente #",
    rfc: "RFC", rfcPart1: "Letras", rfcPart2: "Fecha", rfcPart3: "Homoclave", rfcSub: "Registro Federal de Contribuyentes",
    nombre: "Nombre(s)", apPaterno: "Apellido paterno", apMaterno: "Apellido materno",
    firstName: "First name", middleName: "Middle name", lastName: "Last name",
    phone: "Teléfono", email: "Correo", countryCode: "Código", areaCode: "Área", phoneNumber: "Número",
    address: "Dirección", colony: "Colonia", city: "Ciudad", state: "Estado", country: "País", zip: "Código postal",
    customsPort: "Puerto aduanal", defaultWarehouse: "Bodega default",
    services: "Servicios ofrecidos", status: "Estado", notes: "Notas",
    identity: "Identificación", contactInfo: "Información de contacto", location: "Ubicación",
    fiscal: "Información fiscal", operations: "Operaciones",
    contacts: "Contactos", addContact: "Agregar contacto", removeContact: "Eliminar", noContacts: "No hay contactos agregados",
    documents: "Documentos", addDoc: "Subir documento", noDocuments: "No hay documentos subidos",
    viewDoc: "Ver", downloadDoc: "Descargar", removeDoc: "Eliminar",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", close: "Cerrar",
    confirmDel: "¿Eliminar este agente?", yes: "Sí, eliminar", no: "Cancelar",
    confirmExit: "Tienes cambios sin guardar. ¿Salir sin guardar?", yesExit: "Sí, salir", noExit: "Seguir editando",
    saved: "Agente guardado exitosamente", deleted: "Agente eliminado",
    required: "Requerido", duplicateInit: "Estas iniciales ya existen", duplicateShort: "Este nombre corto ya existe",
    invalidRfc: "Formato RFC inválido", fillRequired: "Por favor llena todos los campos requeridos",
    active: "Activo", inactive: "Inactivo",
    svc_import: "Despacho de Importación", svc_export: "Despacho de Exportación", svc_fumigation: "Fumigación",
    svc_inspection: "Inspección USDA", svc_cold: "Tratamiento en Frío", svc_phyto: "Certificados Fitosanitarios",
    svc_organic: "Documentación Orgánica", svc_transport: "Transporte Transfronterizo",
  },
};

/* Mirror of CustomsBrokerRegistry constants — see note at top of
   file about deliberate duplication. */
const SERVICES = ["import","export","fumigation","inspection","cold","phyto","organic","transport"];
const PORTS = [{key:"pharr",label:"Pharr, TX"},{key:"hidalgo",label:"Hidalgo, TX"},{key:"laredo",label:"Laredo, TX"},{key:"mcallen",label:"McAllen, TX"},{key:"progreso",label:"Progreso, TX"},{key:"reynosa",label:"Reynosa, Tam."},{key:"nuevo_laredo",label:"Nuevo Laredo, Tam."}];
const WAREHOUSES = [{key:"acs",label:"American Cold Storage"},{key:"fb",label:"Five Brothers"}];
const EMPTY = { initials:"", shortName:"", fullName:"", agentType:"mexican", license:"", patente:"", rfcLetters:"", rfcDate:"", rfcHomoclave:"", phoneCountry:"52", phoneArea:"", phoneNumber:"", address:"", colony:"", city:"", state:"", country:"Mexico", zip:"", customsPort:"pharr", defaultWarehouse:"acs", services:[], notes:"", contacts:[], documents:[], isActive:true };
const EMPTY_CONTACT = { nombre:"", apPaterno:"", apMaterno:"", firstName:"", middleName:"", lastName:"", phoneCountry:"", phoneArea:"", phoneNumber:"", email:"" };

const inp = { padding:"8px 10px", fontSize:13, border:"1px solid var(--color-border)", borderRadius:6, background:"var(--color-bg-input)", color:"var(--color-text)", outline:"none", transition:"all 0.2s", fontFamily:"inherit", width:"100%", boxSizing:"border-box" };

/* ---------- helpers (duplicated from CustomsBrokerRegistry) ---------- */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function getSuppliers() { return loadLS("gfi-suppliers", []); }
function getCurrentShipper() {
  var sups = getSuppliers();
  return sups.find(function (s) { return s.supplierType === "shipper"; }) || sups[0] || null;
}
function suggestInitials(name, existing) {
  if (!name || !name.trim()) return "";
  var words = name.trim().split(/\s+/).filter(Boolean);
  var cands = [];
  if (words.length >= 2) {
    cands.push((words[0][0] + words[1][0]).toUpperCase());
    cands.push((words[0][0] + words[1][0] + (words[1][1] || "")).toUpperCase());
  } else if (words.length === 1) {
    cands.push(words[0].slice(0, 2).toUpperCase());
    cands.push(words[0].slice(0, 3).toUpperCase());
  }
  var used = new Set(existing.map(function (s) { return (s || "").toUpperCase(); }));
  for (var i = 0; i < cands.length; i++) { if (!used.has(cands[i])) return cands[i]; }
  return cands[0] || "";
}
function fmtPhone(v) { var d = (v || "").replace(/\D/g, ""); if (d.length <= 4) return d; if (d.length <= 7) return d.slice(0, 3) + "-" + d.slice(3); return d.slice(0, 4) + "-" + d.slice(4, 8); }
function buildRfc(l, d, h) { if (!l && !d && !h) return ""; return [l, d, h].filter(Boolean).join("-"); }
function buildPhone(c, a, n) { if (!c && !a && !n) return ""; return ("+" + (c || "") + " " + (a || "") + " " + (n || "")).trim(); }

/* ---------- shared field shells ---------- */
function Field({ label, required, error, sub, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
        {label}{required && <span style={{ color: "var(--color-danger)", fontSize: 10 }}>*</span>}
      </label>
      {children}
      {sub && <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{sub}</span>}
      {error && <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{error}</span>}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid var(--color-border)", paddingBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}
function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(function () { onClose(); }, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "var(--color-accent)", color: "var(--color-accent-text)", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999999, boxShadow: "0 8px 24px var(--color-shadow)", animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

function PhoneInput({ country, area, number, onChange, t }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "72px 80px 1fr", gap: 6, alignItems: "end" }}>
      <Field label={t.countryCode}><div style={{ position: "relative" }}><span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-text-muted)", pointerEvents: "none" }}>+</span><input type="text" value={country} onChange={function (e) { onChange("phoneCountry", e.target.value.replace(/\D/g, "").slice(0, 3)); }} maxLength={3} style={{ ...inp, paddingLeft: 20, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }} /></div></Field>
      <Field label={t.areaCode}><input type="text" value={area} onChange={function (e) { onChange("phoneArea", e.target.value.replace(/\D/g, "").slice(0, 4)); }} maxLength={4} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }} /></Field>
      <Field label={t.phoneNumber}><input type="text" value={fmtPhone(number)} onChange={function (e) { onChange("phoneNumber", e.target.value.replace(/\D/g, "").slice(0, 8)); }} maxLength={9} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></Field>
    </div>
  );
}

function RfcInput({ letters, date, homo, onChange, errors, t }) {
  var errBorder = errors && errors.rfc ? { borderColor: "var(--color-danger)" } : {};
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 4, display: "block" }}>{t.rfc}</label>
      <div style={{ display: "grid", gridTemplateColumns: "100px 100px 90px", gap: 6 }}>
        <div>
          <input type="text" value={letters} onChange={function (e) { onChange("rfcLetters", e.target.value.toUpperCase().replace(/[^A-ZÑ&]/g, "").slice(0, 4)); }} maxLength={4} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...errBorder }} />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.rfcPart1} (3-4)</div>
        </div>
        <div>
          <input type="text" value={date} onChange={function (e) { onChange("rfcDate", e.target.value.replace(/\D/g, "").slice(0, 6)); }} maxLength={6} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...errBorder }} />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.rfcPart2} (6)</div>
        </div>
        <div>
          <input type="text" value={homo} onChange={function (e) { onChange("rfcHomoclave", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3)); }} maxLength={3} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...errBorder }} />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.rfcPart3} (3)</div>
        </div>
      </div>
      {errors && errors.rfc && <span style={{ fontSize: 11, color: "var(--color-danger)", marginTop: 2, display: "block" }}>{errors.rfc}</span>}
      <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic", marginTop: 2, display: "block" }}>{t.rfcSub}</span>
    </div>
  );
}

function ContactNameFields({ contact, agentType, onChange, t }) {
  if (agentType === "american") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <Field label={t.firstName}><input type="text" value={contact.firstName || ""} onChange={function (e) { onChange("firstName", e.target.value); }} style={inp} /></Field>
        <Field label={t.middleName}><input type="text" value={contact.middleName || ""} onChange={function (e) { onChange("middleName", e.target.value); }} style={inp} /></Field>
        <Field label={t.lastName}><input type="text" value={contact.lastName || ""} onChange={function (e) { onChange("lastName", e.target.value); }} style={inp} /></Field>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
      <Field label={t.nombre}><input type="text" value={contact.nombre || ""} onChange={function (e) { onChange("nombre", e.target.value); }} style={inp} /></Field>
      <Field label={t.apPaterno}><input type="text" value={contact.apPaterno || ""} onChange={function (e) { onChange("apPaterno", e.target.value); }} style={inp} /></Field>
      <Field label={t.apMaterno}><input type="text" value={contact.apMaterno || ""} onChange={function (e) { onChange("apMaterno", e.target.value); }} style={inp} /></Field>
    </div>
  );
}

function DocumentSection({ documents, onChange, t }) {
  var processFiles = function (files) {
    var arr = Array.from(files || []);
    if (arr.length === 0) return;
    var nd = arr.map(function (f) { return { id: Date.now() + Math.random(), name: f.name, data: null }; });
    arr.forEach(function (f, i) {
      var r = new FileReader();
      r.onload = function (ev) { nd[i].data = ev.target.result; onChange([...documents, ...nd]); };
      r.readAsDataURL(f);
    });
    onChange([...documents, ...nd]);
  };
  var handleUpload = function (e) { processFiles(e.target.files); e.target.value = ""; };
  var rm = function (id) { onChange(documents.filter(function (d) { return d.id !== id; })); };
  return (
    <Section title={t.documents + " (" + documents.length + ")"}>
      {documents.length === 0 && <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noDocuments}</div>}
      {documents.map(function (d) {
        return (
          <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--color-bg)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
            <span style={{ flex: 1, fontSize: 12, fontWeight: 500 }}>{d.name}</span>
            {d.data && <>
              <button onClick={function () { window.open(d.data, "_blank"); }} style={{ fontSize: 11, color: "var(--color-accent)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>{t.viewDoc}</button>
              <a href={d.data} download={d.name} style={{ fontSize: 11, color: "var(--color-accent)", fontWeight: 500, textDecoration: "none" }}>{t.downloadDoc}</a>
            </>}
            <button onClick={function () { rm(d.id); }} style={{ fontSize: 11, color: "var(--color-danger)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>{t.removeDoc} ×</button>
          </div>
        );
      })}
      <FileDropZone onFiles={processFiles}>
        <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600, width: "100%" }}>
          <span style={{ fontSize: 16 }}>+</span> {t.addDoc}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" multiple onChange={handleUpload} style={{ display: "none" }} />
        </label>
      </FileDropZone>
    </Section>
  );
}

/* =================== BROKER FORM ===================
   Mirror of CustomsBrokerRegistry.BrokerForm with one
   exception: save stamps createdBy = current shipper id and
   createdAt on new records. Brokers that someone else (Arturo)
   created are shown read-only with a banner explaining why. */
function BrokerForm({ broker, brokers, currentShipperId, t, onSave, onDelete, onCancel }) {
  var isEdit = !!(broker && broker.id);
  var ownedByShipper = !isEdit || String(broker.createdBy) === String(currentShipperId);
  var initialForm = isEdit
    ? { ...broker, contacts: broker.contacts || [], documents: broker.documents || [], phoneCountry: broker.phoneCountry || "52", phoneArea: broker.phoneArea || "", phoneNumber: broker.phoneNumber || "", agentType: broker.agentType || "mexican" }
    : { ...EMPTY };
  var [form, setForm] = useState(initialForm);
  var [errors, setErrors] = useState({});
  var [showDel, setShowDel] = useState(false);
  var [submitted, setSubmitted] = useState(false);
  var [showExit, setShowExit] = useState(false);

  var set = function (k, v) { setForm(function (f) { return { ...f, [k]: v }; }); if (errors[k]) setErrors(function (e) { return { ...e, [k]: null }; }); };
  var existInit = brokers.filter(function (b) { return b.id !== (broker && broker.id); }).map(function (b) { return b.initials; });
  var handleShortName = function (val) {
    set("shortName", val);
    if (!form.initials || form.initials === suggestInitials(form.shortName, existInit)) set("initials", suggestInitials(val, existInit));
  };
  var isMx = form.agentType !== "american";
  var handleType = function (val) {
    set("agentType", val);
    if (val === "mexican") { set("country", "Mexico"); set("phoneCountry", "52"); }
    else { set("country", "United States"); set("phoneCountry", "1"); set("colony", ""); }
  };
  var toggleSvc = function (svc) {
    set("services", form.services.includes(svc) ? form.services.filter(function (s) { return s !== svc; }) : [...form.services, svc]);
  };
  var addContact = function () { set("contacts", [...form.contacts, { ...EMPTY_CONTACT, id: Date.now() }]); };
  var rmContact = function (idx) { set("contacts", form.contacts.filter(function (_, i) { return i !== idx; })); };
  var setContact = function (idx, k, v) { set("contacts", form.contacts.map(function (c, i) { return i === idx ? { ...c, [k]: v } : c; })); };
  var tryCancel = function () {
    var orig = isEdit ? JSON.stringify(broker) : JSON.stringify(EMPTY);
    if (JSON.stringify(form) !== orig) setShowExit(true); else onCancel();
  };
  var validate = function () {
    var e = {};
    if (!form.initials.trim()) e.initials = t.required;
    else {
      var dup = brokers.find(function (b) { return b.initials && b.initials.toUpperCase() === form.initials.toUpperCase() && b.id !== (broker && broker.id); });
      if (dup) e.initials = t.duplicateInit;
    }
    if (!form.shortName.trim()) e.shortName = t.required;
    else {
      var dupS = brokers.find(function (b) { return b.shortName && b.shortName.toLowerCase() === form.shortName.toLowerCase() && b.id !== (broker && broker.id); });
      if (dupS) e.shortName = t.duplicateShort;
    }
    if (!form.fullName.trim()) e.fullName = t.required;
    if (!form.license.trim()) e.license = t.required;
    if (isMx) {
      var has = form.rfcLetters || form.rfcDate || form.rfcHomoclave;
      if (has) {
        if (form.rfcLetters.length < 3 || form.rfcLetters.length > 4) e.rfc = t.invalidRfc;
        else if (form.rfcDate.length !== 6) e.rfc = t.invalidRfc;
        else if (form.rfcHomoclave.length !== 3) e.rfc = t.invalidRfc;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  var handleSave = function () {
    setSubmitted(true);
    if (!validate()) return;
    /* Stamp createdBy/createdAt for new records, preserve them for
       existing ones. Both surface to Arturo's CustomsBrokerRegistry
       unchanged — the only difference is the createdBy field, which
       his registry doesn't currently filter on. */
    var stamped = {
      ...form,
      initials: form.initials.toUpperCase().trim(),
      shortName: form.shortName.trim(),
      fullName: form.fullName.trim(),
      rfc: buildRfc(form.rfcLetters, form.rfcDate, form.rfcHomoclave),
      phone: buildPhone(form.phoneCountry, form.phoneArea, form.phoneNumber),
      id: isEdit ? form.id : Date.now(),
      createdBy: isEdit ? (form.createdBy || currentShipperId) : currentShipperId,
      createdAt: isEdit ? (form.createdAt || new Date().toISOString()) : new Date().toISOString(),
    };
    onSave(stamped);
  };
  var eb = function (k) { return errors[k] ? { borderColor: "var(--color-danger)" } : {}; };

  return (
    <DraggableModal title={isEdit ? t.edit : t.add} onClose={tryCancel} initialWidth={620} initialHeight={Math.min(780, window.innerHeight - 60)}>
      <div style={{ overflow: "auto", flex: 1 }}>
        {!ownedByShipper && (
          <div style={{ padding: "10px 22px", background: "rgba(107,114,128,0.08)", borderBottom: "1px solid var(--color-border)", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", textAlign: "center" }}>
            {t.cantEditBanner}
          </div>
        )}
        {showExit && (
          <div style={{ padding: "12px 22px", background: "rgba(212,148,10,0.08)", borderBottom: "1px solid rgba(212,148,10,0.2)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--color-warning)", fontWeight: 500 }}>{t.confirmExit}</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={onCancel} style={{ padding: "5px 10px", borderRadius: 4, border: "none", background: "var(--color-warning)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yesExit}</button>
              <button onClick={function () { setShowExit(false); }} style={{ padding: "5px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.noExit}</button>
            </div>
          </div>
        )}
        <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 18, opacity: ownedByShipper ? 1 : 0.7, pointerEvents: ownedByShipper ? "auto" : "none" }}>
          {submitted && Object.keys(errors).length > 0 && (
            <div style={{ background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.2)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--color-danger)", fontWeight: 500 }}>{t.fillRequired}</div>
          )}

          <Field label={t.agentType} required>
            <div style={{ display: "flex", border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden" }}>
              {[{ k: "mexican", l: t.mexicanAgent }, { k: "american", l: t.americanAgent }].map(function (at) {
                var active = form.agentType === at.k;
                return (
                  <button key={at.k} onClick={function () { handleType(at.k); }} style={{ flex: 1, padding: "8px 12px", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: active ? "var(--color-accent)" : "transparent", color: active ? "var(--color-accent-text)" : "var(--color-text-muted)", transition: "all 0.15s" }}>{at.l}</button>
                );
              })}
            </div>
          </Field>

          <Section title={t.identity}>
            <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 12 }}>
              <Field label={t.initials} required error={errors.initials}>
                <input type="text" value={form.initials} onChange={function (e) { set("initials", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5)); }} maxLength={5} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textAlign: "center", ...eb("initials") }} />
              </Field>
              <Field label={t.shortName} required error={errors.shortName}>
                <input type="text" value={form.shortName} onChange={function (e) { handleShortName(e.target.value); }} style={{ ...inp, ...eb("shortName") }} />
              </Field>
            </div>
            <Field label={t.fullName} required error={errors.fullName}>
              <input type="text" value={form.fullName} onChange={function (e) { set("fullName", e.target.value); }} style={{ ...inp, ...eb("fullName") }} />
            </Field>
          </Section>

          <Section title={isMx ? (t.fiscal + " / " + t.license) : t.license}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={t.license} required error={errors.license}>
                <input type="text" value={form.license} onChange={function (e) { set("license", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...eb("license") }} />
              </Field>
              <Field label={t.patente}>
                <input type="text" value={form.patente} onChange={function (e) { set("patente", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }} />
              </Field>
            </div>
            {isMx && <RfcInput letters={form.rfcLetters} date={form.rfcDate} homo={form.rfcHomoclave} onChange={function (k, v) { set(k, v); }} errors={errors} t={t} />}
          </Section>

          <Section title={t.contactInfo}>
            <PhoneInput country={form.phoneCountry} area={form.phoneArea} number={form.phoneNumber} onChange={function (k, v) { set(k, v); }} t={t} />
          </Section>

          <Section title={t.contacts + " (" + form.contacts.length + ")"}>
            {form.contacts.length === 0 && <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noContacts}</div>}
            {form.contacts.map(function (c, idx) {
              return (
                <div key={c.id || idx} style={{ background: "var(--color-bg)", borderRadius: 8, padding: 12, border: "1px solid var(--color-border)", display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)" }}>#{idx + 1}</span>
                    <button onClick={function () { rmContact(idx); }} style={{ fontSize: 11, color: "var(--color-danger)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>{t.removeContact} ×</button>
                  </div>
                  <ContactNameFields contact={c} agentType={form.agentType} onChange={function (k, v) { setContact(idx, k, v); }} t={t} />
                  <div style={{ display: "grid", gridTemplateColumns: "72px 80px 1fr", gap: 6 }}>
                    <Field label={t.countryCode}><div style={{ position: "relative" }}><span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--color-text-muted)", pointerEvents: "none" }}>+</span><input type="text" value={c.phoneCountry || ""} onChange={function (e) { setContact(idx, "phoneCountry", e.target.value.replace(/\D/g, "").slice(0, 3)); }} maxLength={3} style={{ ...inp, paddingLeft: 20, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }} /></div></Field>
                    <Field label={t.areaCode}><input type="text" value={c.phoneArea || ""} onChange={function (e) { setContact(idx, "phoneArea", e.target.value.replace(/\D/g, "").slice(0, 4)); }} maxLength={4} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }} /></Field>
                    <Field label={t.phoneNumber}><input type="text" value={fmtPhone(c.phoneNumber || "")} onChange={function (e) { setContact(idx, "phoneNumber", e.target.value.replace(/\D/g, "").slice(0, 8)); }} maxLength={9} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></Field>
                  </div>
                  <Field label={t.email}><input type="email" value={c.email || ""} onChange={function (e) { setContact(idx, "email", e.target.value); }} style={inp} /></Field>
                </div>
              );
            })}
            <button onClick={addContact} style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, justifyContent: "center", width: "100%" }}><span style={{ fontSize: 16 }}>+</span> {t.addContact}</button>
          </Section>

          <Section title={t.location}>
            <Field label={t.address}><input type="text" value={form.address} onChange={function (e) { set("address", e.target.value); }} style={inp} /></Field>
            {isMx && <Field label={t.colony}><input type="text" value={form.colony} onChange={function (e) { set("colony", e.target.value); }} style={inp} /></Field>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px", gap: 12 }}>
              <Field label={t.city}><input type="text" value={form.city} onChange={function (e) { set("city", e.target.value); }} style={inp} /></Field>
              <Field label={t.state}><input type="text" value={form.state} onChange={function (e) { set("state", e.target.value); }} style={inp} /></Field>
              <Field label={t.zip}><input type="text" value={form.zip} onChange={function (e) { set("zip", e.target.value); }} style={inp} /></Field>
            </div>
          </Section>

          <Section title={t.operations}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={t.customsPort}>
                <SearchSelect value={form.customsPort} onChange={function (v) { set("customsPort", v); }} options={PORTS.map(function (p) { return { value: p.key, label: p.label }; })} />
              </Field>
              <Field label={t.defaultWarehouse}>
                <SearchSelect value={form.defaultWarehouse} onChange={function (v) { set("defaultWarehouse", v); }} options={WAREHOUSES.map(function (w) { return { value: w.key, label: w.label }; })} />
              </Field>
            </div>
          </Section>

          <Section title={t.services}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {SERVICES.map(function (svc) {
                var active = form.services.includes(svc);
                return (
                  <label key={svc} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500, background: active ? "var(--color-accent-bg)" : "transparent", border: "1px solid " + (active ? "var(--color-accent)" : "var(--color-border)"), color: active ? "var(--color-accent)" : "var(--color-text-secondary)", transition: "all 0.15s" }}>
                    <input type="checkbox" checked={active} onChange={function () { toggleSvc(svc); }} style={{ accentColor: "var(--color-accent)", margin: 0 }} />
                    {t["svc_" + svc]}
                  </label>
                );
              })}
            </div>
          </Section>

          <Field label={t.notes}><textarea value={form.notes} onChange={function (e) { set("notes", e.target.value); }} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></Field>

          <DocumentSection documents={form.documents || []} onChange={function (docs) { set("documents", docs); }} t={t} />

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={function () { set("isActive", !form.isActive); }} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 20, border: "1px solid " + (form.isActive ? "rgba(39,174,96,0.3)" : "rgba(231,76,60,0.3)"), background: form.isActive ? "rgba(39,174,96,0.08)" : "rgba(231,76,60,0.08)", cursor: "pointer", fontSize: 12, fontWeight: 500, color: form.isActive ? "var(--color-success)" : "var(--color-danger)" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: form.isActive ? "var(--color-success)" : "var(--color-danger)" }} />
              {form.isActive ? t.active : t.inactive}
            </button>
          </div>
        </div>

        <div style={{ padding: "14px 22px 18px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", bottom: 0, background: "var(--color-bg-card)" }}>
          <div>
            {isEdit && ownedByShipper && !showDel && <button onClick={function () { setShowDel(true); }} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.25)", background: "rgba(231,76,60,0.08)", color: "var(--color-danger)", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>{t.delete}</button>}
            {showDel && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "var(--color-danger)" }}>{t.confirmDel}</span>
                <button onClick={function () { onDelete(broker.id); }} style={{ padding: "6px 10px", borderRadius: 4, border: "none", background: "var(--color-danger)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yes}</button>
                <button onClick={function () { setShowDel(false); }} style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.no}</button>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={tryCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{ownedByShipper ? t.cancel : t.close}</button>
            {ownedByShipper && <button onClick={handleSave} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.save}</button>}
          </div>
        </div>
      </div>
    </DraggableModal>
  );
}

/* =================== LIST SHELL =================== */
export default function ShipperBrokersPortal({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [brokers, setBrokers] = useState([]);
  var [search, setSearch] = useState("");
  var [showForm, setShowForm] = useState(false);
  var [editing, setEditing] = useState(null);
  var [toast, setToast] = useState(null);
  var [reloadKey, setReloadKey] = useState(0);

  var shipper = useMemo(function () { return getCurrentShipper(); }, [reloadKey]);

  useEffect(function () { setBrokers(loadLS(STORAGE_KEY, [])); }, [reloadKey]);

  /* Re-pull on focus — Arturo may have added brokers in his
     registry in another tab. */
  useEffect(function () {
    var onFocus = function () { setReloadKey(function (k) { return k + 1; }); };
    window.addEventListener("focus", onFocus);
    return function () { window.removeEventListener("focus", onFocus); };
  }, []);

  var persist = useCallback(function (next) {
    setBrokers(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { setToast("Storage error"); }
  }, []);

  /* Only this shipper's brokers — createdBy match. */
  var myBrokers = useMemo(function () {
    if (!shipper) return [];
    return brokers.filter(function (b) { return String(b.createdBy) === String(shipper.id); });
  }, [brokers, shipper]);

  var filtered = useMemo(function () {
    if (!search) return myBrokers;
    var s = search.toLowerCase();
    return myBrokers.filter(function (b) {
      return (b.shortName || "").toLowerCase().includes(s)
        || (b.fullName || "").toLowerCase().includes(s)
        || (b.city || "").toLowerCase().includes(s)
        || (b.customsPort || "").toLowerCase().includes(s)
        || (b.license || "").toLowerCase().includes(s)
        || (b.initials || "").toLowerCase().includes(s);
    });
  }, [myBrokers, search]);

  var openNew = function () {
    if (!shipper) { alert(lang === "es" ? "No hay shipper configurado." : "No shipper configured."); return; }
    setEditing(null);
    setShowForm(true);
  };
  var openExisting = function (b) { setEditing(b); setShowForm(true); };

  var handleSave = function (b) {
    var exists = brokers.find(function (x) { return x.id === b.id; });
    var next = exists ? brokers.map(function (x) { return x.id === b.id ? b : x; }) : [...brokers, b];
    persist(next);
    setToast(t.saved);
    setShowForm(false); setEditing(null);
  };
  var handleDelete = function (id) {
    persist(brokers.filter(function (x) { return x.id !== id; }));
    setToast(t.deleted);
    setShowForm(false); setEditing(null);
  };

  var formatPhone = function (b) {
    var parts = [b.phoneCountry, b.phoneArea, b.phoneNumber].filter(Boolean);
    return parts.length === 0 ? "—" : "+" + parts.join(" ");
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
          {shipper && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{t.loggedAs}: <strong>{shipper.shortName || shipper.fullName || "—"}</strong> · {shipper.initials || "??"}</div>}
        </div>
        <button onClick={openNew} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>+ {t.add}</button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, maxWidth: 360 }} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14, border: "1px solid var(--color-border)", borderRadius: 10 }}>{t.noResults}</div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "var(--color-bg)", borderBottom: "2px solid var(--color-border)" }}>
              {[t.colInitials, t.colShort, t.colFull, t.colType, t.colPhone, t.colEmail, t.colCountry, t.colCity].map(function (h, i) {
                return <th key={i} style={{ padding: "9px 10px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>;
              })}
            </tr></thead>
            <tbody>{filtered.map(function (b) {
              return (
                <tr key={b.id} onClick={function () { openExisting(b); }} style={{ cursor: "pointer", borderBottom: "1px solid var(--color-border)" }} onMouseEnter={function (e) { e.currentTarget.style.background = "var(--color-bg-hover, rgba(0,0,0,0.02))"; }} onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "9px 10px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{b.initials || "—"}</td>
                  <td style={{ padding: "9px 10px", fontWeight: 600, fontSize: 12 }}>{b.shortName || "—"}</td>
                  <td style={{ padding: "9px 10px", fontSize: 12 }}>{b.fullName || "—"}</td>
                  <td style={{ padding: "9px 10px", fontSize: 11 }}>{b.agentType === "american" ? t.americanAgent : t.mexicanAgent}</td>
                  <td style={{ padding: "9px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{formatPhone(b)}</td>
                  <td style={{ padding: "9px 10px", fontSize: 11, color: "var(--color-text-secondary)" }}>{(b.contacts && b.contacts[0] && b.contacts[0].email) || "—"}</td>
                  <td style={{ padding: "9px 10px", fontSize: 11 }}>{b.country || "—"}</td>
                  <td style={{ padding: "9px 10px", fontSize: 11 }}>{b.city || "—"}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      {showForm && <BrokerForm broker={editing} brokers={brokers} currentShipperId={shipper && shipper.id} t={t} onSave={handleSave} onDelete={handleDelete} onCancel={function () { setShowForm(false); setEditing(null); }} />}
      {toast && <Toast message={toast} onClose={function () { setToast(null); }} />}
    </div>
  );
}
